import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiMinus, FiPlus, FiX } from "react-icons/fi";
import { useCart } from "./CartContext";
import productData, { siteConfig } from "../Datas/productData";
import "../css/cartpage.scss";

const itemKey = (item) => `${item.id}-${item.size}-${item.color}`;

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, cartSubtotal, removeFromCart, updateQuantity } = useCart();
  const [editingKey, setEditingKey] = useState("");

  const detailedItems = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  if (detailedItems.length === 0) {
    return (
      <main className="cart-page cart-page--empty">
        <div className="cart-empty">
          <p className="cart-kicker">Shopping bag</p>
          <h1>Your bag is empty</h1>
          <p>Discover considered pieces from the latest collection.</p>
          <Link className="button button--dark" to="/shop">
            Continue shopping
          </Link>
        </div>
      </main>
    );
  }

  const shipping = siteConfig.standardShipping;
  const total = cartSubtotal + shipping;

  return (
    <main className="cart-page">
      <div className="cart-shell">
        <header className="cart-delivery">
          <h1>Home delivery</h1>
        </header>

        <div className="cart-items">
          {detailedItems.map((item, itemIndex) => {
            const key = itemKey(item);
            const isEditing = editingKey === key;
            const editPanelId = `cart-edit-${item.id}-${itemIndex}`;
            return (
              <article className="cart-item" key={key}>
                <Link to={`/shopDetails/${item.product.id}`} className="cart-item__image">
                  <img
                    src={item.product.image}
                    alt={item.product.name}
                    width="94"
                    height="126"
                    loading="lazy"
                    decoding="async"
                  />
                </Link>
                <div className="cart-item__body">
                  <Link to={`/shopDetails/${item.product.id}`} className="cart-item__name">
                    {item.product.name}
                  </Link>
                  <p>Colour: {item.color || item.product.colors[0]?.name}</p>
                  <p>Size: {item.size}</p>

                  <div className="cart-quantity" aria-label={`Quantity for ${item.product.name}`}>
                    <button
                      type="button"
                      onClick={() =>
                        updateQuantity(item.id, item.size, item.color, item.quantity - 1)
                      }
                      aria-label="Decrease quantity"
                      disabled={item.quantity <= 1}
                    >
                      <FiMinus aria-hidden="true" />
                    </button>
                    <span aria-live="polite">{item.quantity}</span>
                    <button
                      type="button"
                      onClick={() =>
                        updateQuantity(item.id, item.size, item.color, item.quantity + 1)
                      }
                      aria-label="Increase quantity"
                      disabled={item.quantity >= 10}
                    >
                      <FiPlus aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <strong className="cart-item__price">
                  {siteConfig.formatPrice(item.product.price * item.quantity)}
                </strong>

                <div className="cart-item__links">
                  <button
                    type="button"
                    onClick={() => setEditingKey(isEditing ? "" : key)}
                    aria-expanded={isEditing}
                    aria-controls={editPanelId}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => removeFromCart(item.id, item.size, item.color)}
                  >
                    Remove
                  </button>
                </div>

                {isEditing && (
                  <div className="cart-edit" id={editPanelId}>
                    <div className="cart-edit__header">
                      <span>Item options</span>
                      <button type="button" onClick={() => setEditingKey("")} aria-label="Close item options">
                        <FiX aria-hidden="true" />
                      </button>
                    </div>
                    <p>
                      To change size or color, remove this selection and choose a new
                      variation from the product page.
                    </p>
                    <Link to={`/shopDetails/${item.product.id}`}>View product</Link>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <section className="cart-summary" aria-labelledby="summary-heading">
          <h2 className="sr-only" id="summary-heading">Order summary</h2>
          <dl>
            <div>
              <dt>Subtotal</dt>
              <dd>{siteConfig.formatPrice(cartSubtotal)}</dd>
            </div>
            <div>
              <dt>Shipping</dt>
              <dd>{shipping === 0 ? "Complimentary" : siteConfig.formatPrice(shipping)}</dd>
            </div>
            <div>
              <dt>Sales tax</dt>
              <dd>To be calculated</dd>
            </div>
            <div className="cart-summary__total">
              <dt>Total</dt>
              <dd>{siteConfig.formatPrice(total)}</dd>
            </div>
          </dl>
          <button className="button button--dark cart-checkout" type="button" onClick={() => navigate("/checkout")}>
            Checkout
          </button>
          <div className="cart-express" aria-label="Accepted payment methods">
            <span>Visa</span><span>Mastercard</span><span>Amex</span><span>Apple Pay</span>
          </div>
          <p className="cart-summary__notice">By continuing, you agree to the <Link to="/terms">terms of sale</Link>.</p>
          <details className="cart-terms">
            <summary>Commercial terms and conditions</summary>
            <p>Shipping, returns and payment terms are available before you place an order.</p>
          </details>
        </section>
      </div>
    </main>
  );
};

export default CartPage;
